import {
  IDataTableSliceFieldsFragmentWithComparison,
  IParsedChartDataDataTable,
  IParsedChartDataRecord,
  IParsedDataTableColumn,
} from '../../components/Chart/interfaces';
import {
  IDataTableColumn,
  IDataTableColumnFieldsFragment,
  QueryType,
} from '../apollo/graphqlSchema';
import { MaybeNil } from '../interfaces';
import {
  parseAliasReplacements,
  toComparisonAliasableSlice,
  toDefaultHasAlias,
} from './util';
import { IDataTableAnalysis } from '../apollo/responseTypes';
import { assertNotNil } from '../types';
import { mergeDataTableComparisonData } from './mergeDataTableComparisonData';

/* ******************** New data transforming methods for Data Table *************************** */
/**
 * @param slices raw slices from the server
 * @param base base line as slice from server, if exists, combine it into slices
 * @returns combined slices with base and slice from server
 */
const parseSlicesHandler = (
  slices: IDataTableSliceFieldsFragmentWithComparison[],
  base: MaybeNil<IDataTableSliceFieldsFragmentWithComparison>,
) => {
  const parsedSlices: IDataTableSliceFieldsFragmentWithComparison[] = [
    ...slices,
  ];

  // if base and slices both are exist, then we need to add base to slices.
  if (base) {
    // add base as the first slice
    parsedSlices.unshift(base);
  }

  return parsedSlices;
};

// insert measure key, slice key into columns, so we can find column with key in the chart
const parseMeasureColumnsHandler = (
  columns: IDataTableColumnFieldsFragment[],
  slices: IDataTableSliceFieldsFragmentWithComparison[],
): IParsedDataTableColumn[] => {
  return columns.map((column, colIndex) => {
    const slice = slices.find((s) => s.measures.includes(colIndex));
    if (!slice) {
      throw new Error(`No matching slice found for column ${column.key}`);
    }

    return {
      ...column,
      sliceKey: slice.key,
      sliceName: slice.name,
    };
  });
};

const generateChartDataFromColumns = (
  parsedColumns: IDataTableColumn[],
  dates: string[],
  live: boolean[],
): [IParsedChartDataRecord[], Record<string, string[]>] => {
  const chartData: IParsedChartDataRecord[] = [];
  let measureToDataKeyObject: Record<string, string[]> = {};
  for (let i = 0; i < dates.length; i++) {
    const date = dates[i];
    const dataForDay: IParsedChartDataRecord = { date };
    for (const column of parsedColumns) {
      const { key, values, measureKey } = column;
      dataForDay[key] = values[i];
      if (!measureToDataKeyObject[measureKey]) {
        measureToDataKeyObject[measureKey] = [];
      }
      measureToDataKeyObject[measureKey].push(key);
    }
    dataForDay.isLive = live[i];
    chartData.push(dataForDay);
  }
  return [chartData, measureToDataKeyObject];
};

// ******************** End of new data transforming methods for Data Table ***************
export default function transformDataTableData(
  analysis: IDataTableAnalysis,
): IParsedChartDataDataTable {
  assertNotNil(analysis.data);

  // XXX we actually modify the Kobol's response object here to ensure consistent handling of the merged data
  analysis.data = mergeDataTableComparisonData({
    primaryData: analysis.data,
    comparisonData: analysis.comparisonData,
  });

  const { base, columns, dates, slices, live } = analysis.data;

  const measureControls =
    analysis.chartControl.measureControls.map(toDefaultHasAlias);
  const aliasReplacements = parseAliasReplacements(
    analysis.pref.aliasReplacements ?? [],
  );

  const parsedSlices = parseSlicesHandler(slices, base).map((slice) =>
    toComparisonAliasableSlice(slice, slice.isComparison ?? false),
  );
  const parsedColumns = parseMeasureColumnsHandler(columns, parsedSlices);
  const [data] = generateChartDataFromColumns(parsedColumns, dates, live);

  return {
    queryType: QueryType.DataTable,
    columns: parsedColumns,
    slices: parsedSlices,
    data,
    aliasReplacements,
    aliasReplacementsSnapshot: aliasReplacements,
    highlights: [],
    measureControls,
  };
}
